import * as O from 'effect/Option'
import { TFunction } from 'i18next'
import rules, { RègleModèleTravailleurIndépendant } from 'modele-ti'
import Engine from 'publicodes'
import { Trans } from 'react-i18next'

import { PublicodesDoc } from '@/components/documentation'
import { Strong } from '@/design-system'
import { documentationPublicodes } from '@/domaine/documentation/documentationPublicodes'
import { PublicodesAdapter } from '@/domaine/engine/PublicodesAdapter'
import { euros } from '@/domaine/MontantPonctuel'
import {
	eurosParAn,
	eurosParJour,
	eurosParMois,
} from '@/domaine/MontantRecurrent'
import { toOuiNon } from '@/domaine/OuiNon'
import { DottedName } from '@/domaine/publicodes/DottedName'
import {
	pointsParAn,
	quantité,
	trimestresValidésParAn,
} from '@/domaine/Quantite'
import { engineFactory } from '@/utils/publicodes/engineFactory'

import { IRouIS } from './imposition'
import { ModèleComparable, ValeurDocumentée } from './modeleComparable'
import { initialSituationComparée, SituationComparée } from './situation'

const nomModèle = 'modele-ti'

let engine: Engine<DottedName> | null = null

const situationDeBase = {
	'entreprise . date de création': "période . début d'année",
}

const initEngine = () => {
	engine = engineFactory(rules, nomModèle)
	engine.setSituation(situationDeBase)

	return engine
}

const getEngine = () => engine ?? initEngine()

const étiquette = 'EI'

const documentation = (dottedName: RègleModèleTravailleurIndépendant) =>
	documentationPublicodes(getEngine, dottedName, étiquette)

const valeurDocumentée = <V,>(
	dottedName: RègleModèleTravailleurIndépendant,
	défaut: V,
	unité?: string
): V & ValeurDocumentée => ({
	...(engine
		? (O.getOrElse(
				PublicodesAdapter.decode(
					engine.evaluate(unité ? { valeur: dottedName, unité } : dottedName)
				),
				() => défaut
			) as V)
		: défaut),
	documentation: documentation(dottedName),
})

export const ModèleTravailleurIndépendant: ModèleComparable = {
	nom: nomModèle,

	DocumentationDeRègle: PublicodesDoc.documentationDeRègle(
		getEngine,
		nomModèle
	),

	set: {
		situation: (situation: SituationComparée) => {
			const nouvelleSituation = {
				...situationDeBase,
				...(O.isSome(situation.chiffreDAffaires)
					? {
							"entreprise . chiffre d'affaires": PublicodesAdapter.encode(
								situation.chiffreDAffaires
							),
						}
					: {}),
				...(O.isSome(situation.charges)
					? {
							'entreprise . charges': PublicodesAdapter.encode(
								situation.charges
							),
						}
					: {}),
				'entreprise . imposition': PublicodesAdapter.encode(
					O.some(situation.IRouIS)
				),
				'entreprise . activité': PublicodesAdapter.encode(
					O.some(situation.natureActivité)
				),
				...(situation.natureActivité === 'libérale'
					? {}
					: {
							'entreprise . activité . principale': PublicodesAdapter.encode(
								O.some(situation.typeActivité)
							),
						}),
				...(situation.natureActivité === 'libérale'
					? {
							'entreprise . activité . libérale . réglementée':
								PublicodesAdapter.encode(
									O.some(toOuiNon(situation.activitéLibéraleRéglementée))
								),
						}
					: {}),
				'indépendant . cotisations et contributions . cotisations . exonérations . Acre':
					PublicodesAdapter.encode(O.some(toOuiNon(situation.acre))),
				'impôt . méthode de calcul': PublicodesAdapter.encode(
					O.some(situation.méthodeImposition)
				),
				...(O.isSome(situation.tauxImposition)
					? {
							'impôt . taux personnalisé': PublicodesAdapter.encode(
								situation.tauxImposition
							),
						}
					: {}),
				'impôt . foyer fiscal . situation de famille . question':
					PublicodesAdapter.encode(O.some(situation.situationFamiliale)),
				'impôt . foyer fiscal . enfants à charge': PublicodesAdapter.encode(
					O.some(situation.enfants)
				),
				'impôt . foyer fiscal . parent isolé': PublicodesAdapter.encode(
					O.some(toOuiNon(situation.parentIsolé))
				),
				'impôt . foyer fiscal . autres revenus imposables':
					PublicodesAdapter.encode(O.some(situation.autresRevenus)),
			}

			if (!engine) {
				engine = initEngine()
			}

			engine.setSituation(nouvelleSituation)
		},
	},

	get: {
		statut: {
			étiquette,
			nom: 'Entreprise individuelle',
			régime: (t: TFunction) =>
				t(
					'pages.simulateurs.comparaison-statuts.items.statut.régime.travailleur-indépendant',
					'Travailleur indépendant'
				),
			imposition: () => {
				let imposition = initialSituationComparée.IRouIS

				if (engine) {
					const valeur = engine.evaluate('entreprise . imposition')
					imposition = O.getOrElse(
						PublicodesAdapter.decode(valeur),
						() => initialSituationComparée.IRouIS
					) as IRouIS
				}

				return imposition === 'IS' ? (
					<Trans i18nKey="pages.simulateurs.comparaison-statuts.carte.imposition.IS">
						<Strong>Impôt sur les sociétés</Strong> (IS)
					</Trans>
				) : (
					<Trans i18nKey="pages.simulateurs.comparaison-statuts.carte.imposition.IR">
						<Strong>Impôt sur le revenu</Strong> (IR)
					</Trans>
				)
			},
		},

		revenu: () => ({
			bénéfice: valeurDocumentée(
				'indépendant . rémunération . brute',
				eurosParAn(0)
			),
			revenuNet: valeurDocumentée(
				'indépendant . rémunération . nette',
				eurosParMois(0)
			),
			revenuNetAprèsImpôt: valeurDocumentée(
				'indépendant . rémunération . nette . après impôt',
				eurosParMois(0)
			),
		}),

		dépenses: () => ({
			cotisations: valeurDocumentée(
				'indépendant . cotisations et contributions',
				eurosParAn(0)
			),
			impôt: valeurDocumentée(
				'indépendant . rémunération . impôt',
				eurosParMois(0)
			),
		}),

		retraite: () => ({
			documentation: documentation('protection sociale . retraite'),
			trimestres: valeurDocumentée(
				'protection sociale . retraite . base . trimestres',
				trimestresValidésParAn(0)
			),
			revenuCotisé: valeurDocumentée(
				'protection sociale . retraite . base . revenu cotisé',
				eurosParAn(0)
			),
			pointsComplémentaire: valeurDocumentée(
				'protection sociale . retraite . complémentaire . points acquis',
				pointsParAn(0)
			),
			valeurPointComplémentaire: valeurDocumentée(
				'protection sociale . retraite . complémentaire . valeur du point',
				eurosParAn(0),
				'€/an'
			),
		}),

		maladie: () => ({
			documentation: documentation('protection sociale . maladie'),
			indemnitésArrêtMaladie: valeurDocumentée(
				'protection sociale . maladie . arrêt maladie',
				eurosParJour(0)
			),
			délaiAttente: valeurDocumentée(
				"protection sociale . maladie . arrêt maladie . délai d'attente",
				quantité(0, 'mois')
			),
		}),

		parentalité: () => ({
			documentation: documentation('protection sociale . maladie'),
			indemnitésMaternitéPaternitéAdoption: valeurDocumentée(
				'protection sociale . maladie . maternité paternité adoption',
				eurosParJour(0)
			),
			allocationNaissance: valeurDocumentée(
				'protection sociale . maladie . maternité paternité adoption . allocation forfaitaire de repos maternel',
				euros(0)
			),
			allocationAdoption: valeurDocumentée(
				'protection sociale . maladie . maternité paternité adoption . allocation forfaitaire de repos adoption',
				euros(0)
			),
		}),

		invalidité: () => ({
			documentation: documentation('protection sociale . invalidité et décès'),
			pensionInvaliditéPartielle: valeurDocumentée(
				'protection sociale . invalidité et décès . pension invalidité . invalidité partielle',
				eurosParMois(0)
			),
			pensionInvaliditéTotale: valeurDocumentée(
				'protection sociale . invalidité et décès . pension invalidité . invalidité totale',
				eurosParMois(0)
			),
		}),

		décès: () => ({
			documentation: documentation('protection sociale . invalidité et décès'),
			pensionDeRéversion: valeurDocumentée(
				'protection sociale . invalidité et décès . pension de reversion',
				eurosParMois(0)
			),
			capitalDécès: valeurDocumentée(
				'protection sociale . invalidité et décès . capital décès',
				euros(0)
			),
			capitalOrphelin: valeurDocumentée(
				'protection sociale . invalidité et décès . capital décès . orphelin',
				euros(0)
			),
		}),

		// gestion: () => {
		// 	return {
		// 		coûtsDeCréation: euros(22.88),
		// 		statutConjointe: (t: TFunction) => t('pages.simulateurs.comparaison-statuts.items.gestion.conjoint.travailleur-indépendant', 'Conjoint collaborateur ou salarié / Conjointe collaboratrice ou salariée'),
		// 	}
		// },
	},
}
